import { useContext, useState } from 'react'
import { ConfigContext } from '../../store/ConfigContext'
import {
  ACTIONS,
  BUTTONS,
  VERSION,
  EVENT_CATEGORY,
  EVENT_DETAIL_TYPE,
  ENVELOPE_VERSION,
  MODES
} from '../constants'
import eventDetailSchema from '../../assets/eventDetailSchema.json'
import versionSchema from '../../assets/versionSchema.json'
import { Validator } from 'jsonschema'

const validateEvent = (eventDetail) => {
  const validator = new Validator()
  validator.addSchema(versionSchema)
  const result = validator.validate(eventDetail, eventDetailSchema)
  if (result.valid) {
    return eventDetail
  }
  throw {
    message: 'Analytic event is not valid!',
    eventDetail,
    errors: JSON.stringify(
      result.errors.map((error) => ({
        property: error.property,
        message: error.message
      }))
    )
  }
}

const buildEventPayload = (action, activity, control, screen) => {
  const eventDetail = {
    action,
    ...(screen.mode ? { screenMode: screen.mode } : {}),
    screenName: screen.name,
    screenPath: screen.path,
    version: VERSION,
    ...(activity ? { activity: activity } : {}),
    ...(control.detail ? { controlDetail: String(control.detail) } : {}),
    ...(control.name ? { controlName: control.name } : {}),
    ...(control.actionAuxParams
      ? { actionAuxParams: control.actionAuxParams }
      : {})
  }
  return {
    dateTime: new Date().toISOString(),
    eventDetailType: EVENT_DETAIL_TYPE,
    eventCategory: EVENT_CATEGORY,
    version: ENVELOPE_VERSION,
    eventDetail: validateEvent(eventDetail)
  }
}

function useAnalytics(screen, isModal = false, checkDone = true) {
  const { publishCdmEvent } = useContext(ConfigContext)
  const [done, setDone] = useState(false)

  const { activity, screenPath, screenName } = screen

  const _buildEvent = (
    action,
    { name, detail, mode, actionAuxParams, screenIndex }
  ) => {
    if (mode) {
      const modes = Object.values(MODES)
      if (modes.indexOf(mode) === -1) {
        throw new Error('Invalid mode')
      }
    }
    return buildEventPayload(
      action,
      activity,
      {
        name,
        detail,
        actionAuxParams
      },
      {
        name: screenIndex ? screenName.replace('##', screenIndex) : screenName,
        mode,
        path: screenPath
      }
    )
  }

  const _fire = (
    action,
    { name, detail, mode, actionAuxParams, screenIndex },
    metadata = null
  ) => {
    try {
      publishCdmEvent(
        _buildEvent(action, {
          name,
          detail,
          mode,
          actionAuxParams,
          screenIndex
        }),
        metadata
      )
    } catch (error) {
      console.error(error)
    }
  }

  const fireScreenDisplayed = (props = {}, metadata = null) => {
    if (checkDone && done && !props.force) return
    setDone(true)
    _fire(
      isModal ? ACTIONS.MODAL_WINDOW_DISPLAYED : ACTIONS.SCREEN_DISPLAYED,
      props,
      metadata
    )
  }

  const fireControlAccordionExpanded = (name, props = {}) => {
    publishCdmEvent(
      _buildEvent(ACTIONS.CONTROL_ACCORDION_EXPANDED, { name, ...props })
    )
  }

  const buildButtonClickEvent = (name, props = {}) => {
    if (Object.values(BUTTONS).indexOf(name) === -1) {
      throw new Error('Invalid button')
    }
    return _buildEvent(ACTIONS.CONTROL_BUTTON_CLICKED, { name, ...props })
  }

  const fireButtonClick = (name, props = {}) => {
    publishCdmEvent(buildButtonClickEvent(name, props))
  }

  const fireControlDisplayed = (name, props = {}) => {
    _fire(ACTIONS.CONTROL_DISPLAYED, { name, ...props })
  }

  const fireToggleClick = (name, enabled, props = {}) => {
    if (Object.values(BUTTONS).indexOf(name) === -1) {
      throw new Error('Invalid toggle')
    }
    _fire(
      enabled
        ? ACTIONS.CONTROL_TOGGLE_ENABLED
        : ACTIONS.CONTROL_TOGGLE_DISABLED,
      {
        name,
        ...props
      }
    )
  }

  const buildHyperLinkClickEvent = (name, props = {}) => {
    return _buildEvent(ACTIONS.CONTROL_HYPERLINK_CLICKED, { name, ...props })
  }

  const fireHyperLinkClick = (name, props = {}) => {
    publishCdmEvent(buildHyperLinkClickEvent(name, props))
  }

  return {
    buildButtonClickEvent,
    buildHyperLinkClickEvent,
    fireScreenDisplayed,
    fireButtonClick,
    fireToggleClick,
    fireHyperLinkClick,
    fireControlDisplayed,
    fireControlAccordionExpanded
  }
}

export default useAnalytics
